/**
 * Copyright IBM Corp. 2022, 2026
 */

import { Breadcrumbs, Text } from '@hashicorp/mds-react/components'
import Image from 'next/image'
import {
	ExpandableTags,
	type IExpandableTags,
} from 'app/[locale]/(site)/blog/_components/expandable-tags'
import s from './header.module.css'

export interface IHeader {
	title: string
	subtitle?: string
	category: {
		id: string
		title: string
		slug: string
	}
	tags: IExpandableTags['tags']
	authors: {
		name: string
		slug: string
		photo?: {
			url: string
			alt?: string
			width?: number
			height?: number
		}
	}[]
}

export function Header({ title, subtitle, category, tags, authors }: IHeader) {
	return (
		<div>
			<Breadcrumbs.Root className={s.breadcrumbs}>
				<Breadcrumbs.Item text="Blog" url={'/blog'} />
				<Breadcrumbs.Item
					text={category.title}
					url={`/blog/categories/${category.slug}`}
				/>
			</Breadcrumbs.Root>
			<div className={s.text}>
				<Text.DisplayExpressive
					tag="h1"
					color="strong"
					weight="semibold"
					className={s.title}
				>
					{title}
				</Text.DisplayExpressive>
				{subtitle ? (
					<Text.Body className={s.subtitle} color="primary">
						{subtitle}
					</Text.Body>
				) : null}
			</div>
			<div className={s.byline}>
				<ul className={s.authorImages}>
					{authors.map((author) => {
						return (
							<li key={author.name} className={s.authorImage}>
								{author.photo ? (
									<Image
										src={author.photo.url}
										width={author.photo.width}
										height={author.photo.height}
										alt={author.photo.alt}
									/>
								) : (
									<Image
										src={
											'https://www.datocms-assets.com/2885/1753314445-frame.png'
										}
										width={200}
										height={200}
										alt={'Avatar image of blog post author'}
									/>
								)}
							</li>
						)
					})}
				</ul>
				<ul className={s.authors}>
					{authors.map((author, index) => {
						const hasMultipleAuthors = authors.length > 1

						if (!hasMultipleAuthors) {
							return (
								<li key={author.name} className={s.author}>
									<a href={`/blog/authors/${author.slug}`}>
										<Text.Body color="primary" weight="medium" size="200">
											{author.name}
										</Text.Body>
									</a>
								</li>
							)
						} else {
							const isLast = index === authors.length - 1
							const name = `${author.name}${isLast ? '' : ', '}`
							return (
								<li key={author.name} className={s.author}>
									<a href={`/blog/authors/${author.slug}`}>
										<Text.Body color="primary" weight="medium" size="200">
											{name}
										</Text.Body>
									</a>
								</li>
							)
						}
					})}
				</ul>
			</div>

			<ExpandableTags tags={tags} />
		</div>
	)
}
